import type { TokenStorageGateway } from "../application/ports/token-storage-gateway";
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { UnauthorizedError, SessionExpiredError, ForbiddenError } from "../domain/errors/errors";
import { getActiveRole } from "../core/active-role/active-role";
import { getRefreshTokenCookie } from "@utils/access-token-cookie";
import { getOriginalAdminIdFromAccessToken } from "@utils/access-token-jwt";
import { refreshCoordinator } from "./refresh-coordinator";
import { CLIENT_HEADERS } from "@config/client";

// Login is the one route whose 401 must never trigger a refresh: it means "wrong
// password", and refreshing then replaying would swallow the form error. /refresh
// needs no exclusion — it answers 440 when the refresh token is dead, and 440 never
// triggers a refresh.
const LOGIN_ROUTE = "/v1/public/login";

// Refresh is possible only when we hold a refresh token AND we are not
// impersonating: impersonation tokens have a 1h hard expiry and carry no refresh
// token, so on 401 we let the session end (→ return to the admin session) rather
// than refreshing.
const canRefreshSession = (): boolean => getRefreshTokenCookie() !== null && getOriginalAdminIdFromAccessToken() === null;

// Server-wide rule: 401 is what a refresh repairs, 440 is what it does not. So a
// 440 from /refresh means the refresh token itself is dead → the session is over.
// (A 401 is treated the same here: on /refresh there is nothing left to repair.)
// Anything else is transient: a 429 — the route is rate-limited to 10 calls/min per
// IP, shared by everyone behind the same NAT — a 5xx or a network outage.
// Terminating on those would throw away a still valid refresh token and force a
// needless re-login, so the session is kept and the next call retries. A non-HTTP
// failure only ends the session when no refresh token is left to present (the
// coordinator refuses to call without one).
const refreshFailureEndsSession = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return getRefreshTokenCookie() === null;
  const status = error.response?.status;
  return status === 440 || status === 401;
};

export abstract class HttpGateway {
  protected _instance: AxiosInstance;

  constructor(
    baseURL: string,
    private tokenStorageGateway: TokenStorageGateway,
  ) {
    HttpGateways.addGateway(this);
    this._instance = this.createInstance(baseURL);
  }

  private createInstance(baseURL: string): AxiosInstance {
    // X-Client / X-Client-Version on every call: the backend classifies the caller
    // and knows which build raised an error (#350, #351).
    const instance = axios.create({ baseURL, headers: { ...CLIENT_HEADERS } });
    this.setBearerToken(instance);
    this.addActiveRoleInterceptor(instance);
    this.addLogoutTokenInterceptor(instance);
    return instance;
  }

  private addActiveRoleInterceptor(instance: AxiosInstance) {
    instance.interceptors.request.use(config => {
      const activeRole = getActiveRole();
      if (activeRole) config.headers.set("X-Active-Role", activeRole);
      else config.headers.delete("X-Active-Role");
      return config;
    });
  }

  public setBearerToken(instance: AxiosInstance = this._instance): void {
    const token = this.tokenStorageGateway.retrieveAccessToken();
    // Delete the header when there is no token so a cleared/rotated-away token is
    // never replayed (e.g. after clearing the impersonation access token).
    if (token) instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete instance.defaults.headers.common["Authorization"];
  }

  private addLogoutTokenInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use(
      res => res,
      async (error: AxiosError) => {
        const status = error.response?.status;
        const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
        const isLoginCall = original?.url?.includes(LOGIN_ROUTE) ?? false;

        // On a first 401, try to transparently refresh the access token and replay
        // the request. Guarded so a failed login stays a failed login, so we never
        // retry twice, and so an impersonation session is never refreshed.
        if (status === 401 && original && !original._retried && !isLoginCall && canRefreshSession()) {
          original._retried = true;
          try {
            await refreshCoordinator.refresh();
          } catch (refreshError) {
            if (refreshFailureEndsSession(refreshError)) throw new SessionExpiredError();
            // Transient refresh failure: surface the original error so the caller
            // fails on its own terms, session and refresh token untouched.
            throw error;
          }
          // Drop the stale bearer header so the replayed request picks up the
          // freshly rotated token from instance.defaults.
          original.headers?.delete("Authorization");
          try {
            return await instance.request(original);
          } catch (replayError) {
            // A replay that still 401s (mapped to UnauthorizedError below, _retried
            // blocking a second refresh) means the fresh token is not the problem →
            // the session is over. Any other failure belongs to the caller.
            if (replayError instanceof UnauthorizedError) throw new SessionExpiredError();
            throw replayError;
          }
        }

        if (status === 403) {
          throw new ForbiddenError();
        }
        if (status === 401) {
          throw new UnauthorizedError();
        }
        if (status === 440) {
          throw new SessionExpiredError();
        }
        throw error;
      },
    );
  }
}

export class HttpGateways {
  private static _gateways: HttpGateway[] = [];

  public static addGateway(gateway: HttpGateway): void {
    this._gateways.push(gateway);
  }

  public static setBearerToken() {
    for (const gateway of this._gateways) {
      gateway.setBearerToken();
    }
  }
}
